import {useIntl} from 'react-intl'
import {useParams, useLocation, useRouteMatch} from 'react-router-dom'
import {BRAND_LABEL} from '../brand'

/**
 * Returns meta title and description for FAQ category pages.
 * @returns {{metaTitle: string, metaDescription: string}}
 */
export const useCategoryMeta = () => {
    const {formatMessage} = useIntl()
    const {category_name} = useParams()

    const displayCategoryName = category_name?.replace(/-/g, ' ')

    const metaTitle = displayCategoryName
        ? formatMessage(
              {
                  id: 'faq_page.meta.title.category',
                  defaultMessage: 'Frequently Asked Questions about {brandName} {categoryName}',
              },
              {brandName: BRAND_LABEL, categoryName: displayCategoryName},
          )
        : ''

    const metaDescription = displayCategoryName
        ? formatMessage(
              {
                  id: 'faq_page.meta.description.category',
                  defaultMessage:
                      'Find here all the answers to frequently asked questions about {brandName} {categoryName}. If you need further assistance contact our support team via online chat or phone.',
              },
              {brandName: BRAND_LABEL, categoryName: displayCategoryName},
          )
        : ''

    return {metaTitle, metaDescription}
}

/**
 * Returns meta title and description for the main FAQ page.
 * @returns {{metaTitle: string, metaDescription: string}}
 */
export const useFaqPageMeta = () => {
    const {formatMessage} = useIntl()
    const location = useLocation()
    const {path} = useRouteMatch()

    const defaultMetaTitle = formatMessage(
        {
            id: 'faq_page.search.title',
            defaultMessage: 'Frequent Questions | {brandName}',
        },
        {brandName: BRAND_LABEL},
    )
    const defaultMetaDescription = formatMessage({
        id: 'faq_page.meta.description.default',
        defaultMessage:
            'Find here all the answers to frequently asked questions. If you need further assistance contact our support team via online chat or phone.',
    })

    // Only show default meta if on the main FAQ page
    const isMainFaqPage = location.pathname === path

    return {
        metaTitle: defaultMetaTitle,
        metaDescription: defaultMetaDescription,
        isMainFaqPage,
    }
}

/**
 * Returns meta title and description for a specific FAQ article page.
 * @param {object} article - The article object containing the title.
 * @returns {{metaTitle: string, metaDescription: string}}
 */
export const useArticleMeta = (article) => {
    const {formatMessage} = useIntl()

    const metaTitle = article
        ? formatMessage(
              {
                  id: 'faq_page.meta.title.article',
                  defaultMessage: '{articleTitle} FAQ | {brandName}',
              },
              {brandName: BRAND_LABEL, articleTitle: article.article_title},
          )
        : ''

    const metaDescription = article
        ? formatMessage(
              {
                  id: 'faq_page.meta.description.article',
                  defaultMessage:
                      '{articleTitle} Find the answer to this and any other frequently asked questions in the customer support section of our website.',
              },
              {articleTitle: article.article_title},
          )
        : ''

    return {metaTitle, metaDescription}
}

/**
 * Returns meta title and description for the manuals page.
 * @param {boolean} isNotRootCategory - Flag to determine if it's a subcategory page.
 * @param {object} categoryDetails - The details of the current category.
 * @returns {{metaTitle: string, metaDescription: string}}
 */
export const useManualsPageMeta = (isNotRootCategory, categoryDetails) => {
    const {formatMessage} = useIntl()

    let metaTitle = ''
    let metaDescription = ''

    if (isNotRootCategory && categoryDetails) {
        metaTitle = formatMessage(
            {
                id: 'manuals_page.meta.title.subcategory',
                defaultMessage: 'Product Manuals: Read Online or Download PDF | {brandName}',
            },
            {brandName: BRAND_LABEL},
        )
        metaDescription = formatMessage(
            {
                id: 'manuals_page.meta.description.subcategory',
                defaultMessage:
                    'Here you can find all the instruction manuals for all {brandName} products. View them online or download them in PDF format to always have them with you!',
            },
            {brandName: BRAND_LABEL},
        )
    } else {
        metaTitle = formatMessage(
            {
                id: 'manuals_page.meta.title.default',
                defaultMessage: 'Product Manuals: Read Online or Download PDF | {brandName}',
            },
            {brandName: BRAND_LABEL},
        )
        metaDescription = formatMessage(
            {
                id: 'manuals_page.meta.description.default',
                defaultMessage:
                    'Here you can find all the instruction manuals for all {brandName} products. View them online or download them in PDF format to always have them with you!',
            },
            {brandName: BRAND_LABEL},
        )
    }

    return {metaTitle, metaDescription}
}

/**
 * Returns meta title and description for the Product Registration page.
 * @returns {{metaTitle: string, metaDescription: string}}
 */

export const useProductRegistrationPageMeta = () => {
    const {formatMessage} = useIntl()

    const metaTitle = formatMessage(
        {
            id: 'product_registration.meta.title',
            defaultMessage: 'Product Registration | {brandName}',
        },
        {brandName: BRAND_LABEL},
    )

    const metaDescription = formatMessage({
        id: 'product_registration.meta.description',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })

    return {metaTitle, metaDescription}
}

/**
 * Returns meta title and description for the Officina product configuration page.
 * @param {object} product - The product object.
 * @returns {{metaTitle: string, metaDescription: string}}
 */
export const useOfficinaPageMeta = (product) => {
    const {formatMessage} = useIntl()

    const metaTitle = product?.name
        ? formatMessage(
              {
                  id: 'officina.page.meta.title',
                  defaultMessage: '{productName} - Officina | De’Longhi',
              },
              {productName: product.name},
          )
        : formatMessage({
              id: 'officina.page.meta.title.default',
              defaultMessage: 'Product details | De’Longhi',
          })

    const metaDescription = product?.name
        ? formatMessage(
              {
                  id: 'officina.page.meta.description',
                  defaultMessage: '{productName} - Officina | De’Longhi',
              },
              {productName: product.name},
          )
        : formatMessage({
              id: 'officina.page.meta.description.default',
              defaultMessage: 'Product details | De’Longhi',
          })

    return {metaTitle, metaDescription}
}
